import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import App from './App';
import { ThemeProvider } from './contexts/ThemeContext';
import ErrorBoundary from './components/ErrorBoundary';
import { initSentry } from './utils/sentry';
import { installFrontendErrorReporter } from './utils/errorReporter';
import './styles/theme-variables.css';

// Initialize Sentry error monitoring (async, non-blocking)
initSentry();

// 2026-05-26 — install the frontend error reporter (forwards crashes +
// unhandled promise rejections + failed API calls to /api/frontend-error
// so the Day Dashboard can correlate them with backend request_ids).
installFrontendErrorReporter();

// Unregister service workers and clear caches to prevent ChunkLoadError
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(registration => registration.unregister());
  });
}

// Clear old caches
if ('caches' in window) {
  caches.keys().then(names => {
    names.forEach(name => caches.delete(name));
  });
}

// Lazy load MobileApp for better performance
const MobileApp = lazy(() => import('./mobile/MobileApp'));

// Simple loading component
const LoadingScreen = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    background: '#f4f6f9'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: 40,
        height: 40,
        border: '3px solid #e5e5e5',
        borderTopColor: '#0176d3',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        margin: '0 auto 12px'
      }} />
      <span style={{ color: '#0176d3' }}>טוען...</span>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  </div>
);

// Root component with routing for mobile vs desktop
const RootApp = () => {
  return (
    <Routes>
      {/* Mobile routes */}
      <Route path="/mobile/*" element={
        <Suspense fallback={<LoadingScreen />}>
          <MobileApp />
        </Suspense>
      } />

      {/* Desktop routes - handled by App */}
      <Route path="/*" element={<App />} />
    </Routes>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <BrowserRouter>
    <ErrorBoundary>
      <ThemeProvider>
        <RootApp />
        {/* UX Performance Initiative P1 (owner-spec 2026-06-09):
            global Toaster — RTL, top-center, themed like the rest
            of the app. Mounted at root so detached async work
            (e.g. ProductsModal save running after the modal
            unmounts) can still surface success/error to the user. */}
        <Toaster
          position="top-center"
          containerStyle={{ direction: 'rtl', fontSize: 14 }}
          toastOptions={{
            duration: 3000,
            style: {
              background: '#fff',
              color: '#0c4a6e',
              border: '1px solid #d8dde6',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            },
            success: { iconTheme: { primary: '#04844b', secondary: '#fff' } },
            error: { duration: 5000, iconTheme: { primary: '#c23934', secondary: '#fff' } },
            loading: { iconTheme: { primary: '#0176d3', secondary: '#fff' } },
          }}
        />
      </ThemeProvider>
    </ErrorBoundary>
  </BrowserRouter>
);
